/**
 * Whether the site is running inside the Honeyguide app shell, and the few
 * things that change when it is. This module is the only place the frontend
 * asks — see `docs/native_shell.md`.
 *
 * The shell is a Capacitor app that loads the live site remotely, so the same
 * bundle runs in a browser and in the shell, and a shell of any age may load
 * the newest site. Everything here therefore degrades to browser behaviour
 * when a signal or a plugin is missing.
 *
 * Two signals, either one enough:
 *  - `window.Capacitor.isNativePlatform()`, which Capacitor's native bridge
 *    injects into every page the webview loads, whatever its origin.
 *  - `Honeyguide-iOS/<version>` in the user agent, which the shell appends
 *    (`appendUserAgent`). It is also what the backend reads to give the shell a
 *    long session, and it carries the version, so it is the one to trust for
 *    "which shell". It is what devtools can fake, which makes the shell's CSS
 *    checkable in a desktop browser.
 */

// The same marker `session_management.NATIVE_SHELL_UA_MARKER` matches.
const SHELL_UA = /\bHoneyguide-iOS\/([^\s;)]+)/

export const NATIVE_SHELL_CLASS = 'native-shell'

interface CapacitorPlugin {
  [method: string]: unknown
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean
  isPluginAvailable?: (name: string) => boolean
  Plugins?: Record<string, CapacitorPlugin | undefined>
}

declare global {
  interface Window {
    Capacitor?: CapacitorGlobal
  }
}

function capacitor(): CapacitorGlobal | undefined {
  return typeof window === 'undefined' ? undefined : window.Capacitor
}

function userAgent(): string {
  return typeof navigator === 'undefined' ? '' : navigator.userAgent
}

export function isNativeShell(): boolean {
  try {
    if (capacitor()?.isNativePlatform?.() === true) return true
  } catch {
    // A half-initialised bridge is not a reason to break the page.
  }
  return SHELL_UA.test(userAgent())
}

/** The shell's app version from its user agent, or null outside the shell. */
export function nativeShellVersion(): string | null {
  return SHELL_UA.exec(userAgent())?.[1] ?? null
}

/**
 * Once, at startup: put `.native-shell` on `<html>` so the shell-only CSS
 * applies, and extend the page under the status bar and home indicator.
 *
 * `viewport-fit=cover` is added here rather than written into `index.html`
 * because it is not a no-op in a browser: mobile Safari in landscape on a
 * notched iPhone letterboxes the page into the safe area by default, and with
 * `cover` it would run content under the notch instead. In the shell the
 * `env(safe-area-inset-*)` padding in `input.css` is what keeps content clear,
 * and those insets only become non-zero once the page is `cover`.
 *
 * `maximum-scale=1` is shell-only for the same reason. WKWebView zooms the
 * page in when a control smaller than 16px takes focus, as Safari does, but
 * in the shell there is no pinch to undo it and the page stays zoomed. The
 * cap stops that zoom (and pinch zoom, which the app shouldn't have anyway).
 * In a browser the same cap would take pinch zoom away from users who rely
 * on it, so `index.html` keeps the plain viewport.
 */
export function markNativeShell(): void {
  if (!isNativeShell()) return
  document.documentElement.classList.add(NATIVE_SHELL_CLASS)
  const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
  if (!viewport) return
  const extras = [
    ['viewport-fit', 'cover'],
    ['maximum-scale', '1'],
  ].filter(([key]) => !new RegExp(`${key}\\s*=`).test(viewport.content))
  if (extras.length) {
    viewport.content = [viewport.content, ...extras.map(([k, v]) => `${k}=${v}`)].join(', ')
  }
}

/**
 * The Browser plugin, if this shell has it. Reached through the bridge's
 * global rather than an `@capacitor/browser` import, so the site carries no
 * native package whose version has to match the installed shell's, and a
 * shell without the plugin is simply one where this returns undefined.
 */
function browserPlugin(): { open: (opts: { url: string }) => Promise<void> } | undefined {
  const cap = capacitor()
  if (!cap) return undefined
  try {
    if (cap.isPluginAvailable && !cap.isPluginAvailable('Browser')) return undefined
    const plugin = cap.Plugins?.Browser
    if (plugin && typeof plugin.open === 'function') {
      return plugin as { open: (opts: { url: string }) => Promise<void> }
    }
  } catch {
    // Fall through to the browser path.
  }
  return undefined
}

function openInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}

/**
 * Open a link to another site: the system browser sheet in the shell, a new
 * tab everywhere else. A link inside the webview would turn the app into a
 * browser, which is both a poor experience and an App Review rejection.
 */
export function openExternal(url: string): void {
  const browser = isNativeShell() ? browserPlugin() : undefined
  if (!browser) {
    openInNewTab(url)
    return
  }
  browser.open({ url }).catch(() => openInNewTab(url))
}

/**
 * `onClick` for an `<a href target="_blank">` pointing at another site.
 *
 * In a browser it does nothing, so the link stays a real link: middle-click,
 * modifier-click, "copy link" and screen readers all work as before. In the
 * shell a plain click is taken over and sent through `openExternal`.
 */
export function interceptExternalLinkClick(event: {
  currentTarget: HTMLAnchorElement
  button: number
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
  defaultPrevented: boolean
  preventDefault: () => void
}): void {
  if (!isNativeShell()) return
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  event.preventDefault()
  openExternal(event.currentTarget.href)
}

// --- Push notifications ------------------------------------------------------
//
// Reached through the bridge like the Browser plugin above, so a shell built
// before push existed is one where `pushPlugin()` is undefined and every
// function here answers "unavailable" without touching the network.

export type PushPermission = 'granted' | 'denied' | 'prompt' | 'unavailable'

export interface PushRegistration {
  /** The APNs device token, hex, as the plugin reports it. */
  token: string
  /** The bundle id the token was issued for (`cool.honeyguide.app[.dev]`). */
  appId: string
}

interface ListenerHandle {
  remove: () => Promise<void>
}

interface PushPlugin extends CapacitorPlugin {
  checkPermissions: () => Promise<{ receive: string }>
  requestPermissions: () => Promise<{ receive: string }>
  register: () => Promise<void>
  addListener: (
    event: 'registration' | 'registrationError',
    listener: (payload: { value?: string; error?: string }) => void,
  ) => Promise<ListenerHandle>
}

interface AppPlugin extends CapacitorPlugin {
  getInfo: () => Promise<{ id: string }>
}

interface AppUrlPlugin extends CapacitorPlugin {
  getLaunchUrl: () => Promise<{ url?: string } | null | undefined>
  addListener: (
    event: 'appUrlOpen',
    listener: (payload: { url?: string }) => void,
  ) => Promise<ListenerHandle>
}

const REGISTRATION_TIMEOUT_MS = 20_000

function shellPlugin<T extends CapacitorPlugin>(name: string, methods: string[]): T | undefined {
  if (!isNativeShell()) return undefined
  const cap = capacitor()
  if (!cap) return undefined
  try {
    if (cap.isPluginAvailable && !cap.isPluginAvailable(name)) return undefined
    const plugin = cap.Plugins?.[name]
    if (plugin && methods.every((m) => typeof plugin[m] === 'function')) return plugin as T
  } catch {
    // A broken bridge reads as "no plugin".
  }
  return undefined
}

function pushPlugin(): PushPlugin | undefined {
  return shellPlugin<PushPlugin>('PushNotifications', [
    'checkPermissions',
    'requestPermissions',
    'register',
    'addListener',
  ])
}

function toPermission(state: string | undefined): PushPermission {
  if (state === 'granted' || state === 'denied') return state
  return 'prompt'
}

/** Whether push can be registered right now, or 'unavailable' outside a shell that has the plugin. */
export async function pushPermission(): Promise<PushPermission> {
  const plugin = pushPlugin()
  if (!plugin) return 'unavailable'
  try {
    return toPermission((await plugin.checkPermissions()).receive)
  } catch {
    return 'unavailable'
  }
}

/** Show the OS prompt. Only from an explicit user action; never on page load. */
export async function requestPushPermission(): Promise<PushPermission> {
  const plugin = pushPlugin()
  if (!plugin) return 'unavailable'
  try {
    return toPermission((await plugin.requestPermissions()).receive)
  } catch {
    return 'unavailable'
  }
}

/** The bundle id of the running shell, or null where the App plugin is missing. */
export async function nativeAppId(): Promise<string | null> {
  const plugin = shellPlugin<AppPlugin>('App', ['getInfo'])
  if (!plugin) return null
  try {
    return (await plugin.getInfo()).id || null
  } catch {
    return null
  }
}

/**
 * Ask the OS for a device token. Resolves null when this shell can't (no
 * plugin, no App plugin to name the bundle), rejects on `registrationError`
 * or when APNs doesn't answer in time. Assumes permission is already granted;
 * without it iOS registers silently but delivers nothing.
 */
export async function registerForPush(): Promise<PushRegistration | null> {
  const plugin = pushPlugin()
  if (!plugin) return null
  const appId = await nativeAppId()
  if (!appId) return null

  // Listeners first: the plugin doesn't retain the event for a late listener.
  const token = await new Promise<string>((resolve, reject) => {
    const handles: Promise<ListenerHandle>[] = []
    let settled = false
    const finish = (outcome: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      for (const handle of handles) handle.then((h) => h.remove()).catch(() => {})
      outcome()
    }
    const timer = setTimeout(
      () => finish(() => reject(new Error('Push registration timed out'))),
      REGISTRATION_TIMEOUT_MS,
    )
    handles.push(
      plugin.addListener('registration', ({ value }) =>
        finish(() => (value ? resolve(value) : reject(new Error('Push registration returned no token')))),
      ),
      plugin.addListener('registrationError', ({ error }) =>
        finish(() => reject(new Error(error || 'Push registration failed'))),
      ),
    )
    Promise.all(handles)
      .then(() => plugin.register())
      .catch((err) => finish(() => reject(err instanceof Error ? err : new Error(String(err)))))
  })
  return { token, appId }
}

// --- Universal links ---------------------------------------------------------

/**
 * Run `handler` for each URL the OS hands the shell: a universal link tapped
 * in another app (`appUrlOpen`), including the one the app was launched with
 * (`getLaunchUrl`, in case the launch event fired before the page listened).
 * The shell does not navigate to the URL itself; the handler decides what it
 * means. Returns the unsubscribe. Outside the shell, or in one without the
 * App plugin, nothing is registered and the browser's own link handling
 * applies.
 *
 * A URL identical to the last one delivered is dropped, which is how the
 * launch URL arriving by both routes becomes one call.
 */
export function onAppUrlOpen(handler: (url: string) => void): () => void {
  const plugin = shellPlugin<AppUrlPlugin>('App', ['getLaunchUrl', 'addListener'])
  if (!plugin) return () => {}

  let active = true
  let lastUrl: string | undefined
  const deliver = (url: string | undefined) => {
    if (!active || !url || url === lastUrl) return
    lastUrl = url
    handler(url)
  }

  const handle = plugin.addListener('appUrlOpen', ({ url }) => deliver(url))
  handle.catch(() => {})
  plugin
    .getLaunchUrl()
    .then((launch) => deliver(launch?.url))
    .catch(() => {})

  return () => {
    active = false
    handle.then((h) => h.remove()).catch(() => {})
  }
}
